import Chroma from 'chroma-js';
import { createColorSwatch, finalizeColorSwatch } from './colorSwatch';
import luminanceScales from './luminanceScales';

/**
 * createColorStack is used to convert a seedColor into a stack of 11 colors, ranging the full luminance scale. These different luminance values have been carefully calibrated to help pass WCAG standards in Carina components. The name of each color in a stack is referred to as its "nominalContrast." nominalContrasts include "zero", "10", "15", "30", "45", "70", "100", "150", and "max". For example, a nominalContrast of "70", will have about a 7:1 contrast ratio (WCAG AAA rating) to the "zero" color, and a nominalContrast of "45" will have about a 4.5:1 contrast ratio (WCAG AA rating) to the "zero" color.
 
 * @param {string} seedColor CSS formated color value: hex ('#FFFFFF'), RGB ('rgb(255,255,255)'), HSL ('hsl(360,0%,100%)'), web color ('white'), etc. This is the color that all other colors in the stack will be based.
 
 * @param {string|string[]} label The name for the stack. A stack can have more than one label, for example Carina uses the same colors for the 'brand' and 'interactive' stacks, which would be written ['brand', 'interactive'].
 
 * @param {boolean} useExact Every color generate in a stack will have its luminance adjusted to a specific value: If you're building a color stack that needs to use the seedColor exactly as specified, set useExact to true. The exact seedColor will be assigned to the nominalContrast it matches most closely. Whenever possible, prefer useExact = false, so that standard Carina luminance values will be used.

 * @param {object} overrideColors Sometimes more finite control over any color in the stack is needed. Example:
  { dark: { 10: 'rgb(5,5,5)'}, default: { zero: 'rgb(255,255,245)', 10: 'rgb(240,240,232)'}}

 * @returns {object} complexColorStack
 {
   default: complexColorSwatch[], // read-only
   dark: complexColorSwatch[], // read-only
   seedColor: string, // read-only
   label: string|string[]
 }
  Most base level properties in a complexColorStack are read-only (the exception is 'label', which can be mutated safely). Create a new complexColorStack instead of mutating. A complexColorStack can be serialized with JSON.stringify(). A complexColorStack is most often provided to createColorPalette as an parameter in order to create a Carina palette. 

 * @example 
  const neutralComplexColorStack = createColorStack('grey', neutral);
  const brandComplexColorStack = createColorStack('#86c831', standardLabels.brand, true);
  const tertiaryComplexColorStack = createColorStack(...standardConstructors.tertiary);
  const accentComplexColorStack = createColorStack(
    'hsl(176, 49%, 51%)', 
    'accent', 
    false, 
    {
      default: {
        10: 'hsl(177, 54%, 93%)',
        15: 'hsl(177, 66%, 79%)',
        30: 'hsl(176, 49%, 51%)',
        45: 'hsl(176, 60%, 39%)',
        70: 'hsl(176, 72%, 24%)',
        100: 'hsl(176, 69%, 14%)',
        150: 'hsl(176, 65%, 10%)'
      }
    }
  );
  const brandFinalizedColorStack = finalizeColorStack(brandComplexColorStack);
 */
export function createColorStack(
  seedColor,
  label,
  useExact = false,
  overrideColors = {}
) {
  if (Chroma.valid(seedColor)) {
    const seed = new Chroma(seedColor).css();
    const defaultSwatches = buildSwatchesWithExact(
      seed,
      'default',
      useExact,
      overrideColors.default
    );

    const darkSwatches = buildSwatchesWithExact(
      seed,
      'dark',
      useExact,
      overrideColors.dark
    );

    return {
      label,
      get seedColor() {
        return seed;
      },
      get default() {
        return defaultSwatches;
      },
      get dark() {
        return darkSwatches;
      }
    };
  }
}

export default createColorStack;

/**
 * @function finalizeColorStack returns a clearer and easier to use object containing only nominalContrast properties and final color values for default and dark themes. Example usage: finalizeColorStack(complexColorStack('#999999', 'neutral)); Note: A finalizedColorStack CANNOT be used as an argument to createColorPalette().
 */
export function finalizeColorStack({
  default: defaultSwatches,
  dark: darkSwatches,
  ...restStack
}) {
  return {
    default: simplifySwatches(defaultSwatches),
    dark: simplifySwatches(darkSwatches),
    ...restStack
  };
}

function buildSwatchesWithExact(seed, themeName, useExact, overrideColors) {
  const swatchArray = buildSwatchArray(seed, themeName, overrideColors);
  const exactIndex = findIndexOfClosestContrast(swatchArray, seed);
  swatchArray[exactIndex].useExact = useExact;
  swatchArray[exactIndex].exactColor = seed;
  return swatchArray;
}

function findIndexOfClosestContrast(swatches, seed) {
  const contrastToSeed = swatches.reduce(
    (accumulator, { generatedColor }) => [
      ...accumulator,
      Chroma.contrast(seed, generatedColor)
    ],
    []
  );
  return contrastToSeed.indexOf(Math.min(...contrastToSeed));
}

function buildSwatchArray(
  seedColor,
  themeName = 'default',
  overrideColors = {}
) {
  return Object.keys(luminanceScales[themeName])
    .sort(sortScaleKeys)
    .map(nominalContrast =>
      createColorSwatch(
        seedColor,
        themeName,
        nominalContrast,
        overrideColors[nominalContrast]
      )
    );
}

/* swatches will be provided out of intuitive order unless sorted */
function sortScaleKeys(_a, _b) {
  const a = sortValueReplacement(_a);
  const b = sortValueReplacement(_b);
  if (a < b) {
    return -1;
  } else if (a > b) {
    return 1;
  } else if (a === b) {
    return 0;
  }
}

function sortValueReplacement(value) {
  switch (value) {
    case 'zero':
      return 0;
    case 'max':
      return Number.MAX_SAFE_INTEGER;
    default:
      return parseInt(value, 10);
  }
}

function simplifySwatches(themedSwatches) {
  return themedSwatches.reduce(
    (accumulator, swatch) => ({
      ...accumulator,
      ...finalizeColorSwatch(swatch)
    }),
    {}
  );
}
